import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  BusinessContextResponse,
  BusinessDriverLocation,
  DispatchCandidate,
  DispatchRunSummary,
} from "@ghalishor/contracts";
import type { IdentityApiClient } from "./auth/api-client";
import { identityErrorMessage } from "./auth/api-client";
import { IDENTITY_WEB_ROUTES, type IdentityWebRoute } from "./auth/web-flow";
import { ConsoleNav, Message } from "./business-console";
import { JalaliDateField } from "./jalali-date-field";
import {
  formatJalaliDateTime,
  formatJalaliGregorianDate,
  jalaliPartsToGregorianDate,
  nowJalaliParts,
} from "./jalali";

const STATUS_FA: Record<string, string> = {
  offline: "آفلاین",
  available: "آماده",
  on_route: "در مسیر",
  paused: "توقف موقت",
  planned: "برنامه‌ریزی‌شده",
  assigned: "ارجاع‌شده",
  in_progress: "در حال انجام",
  completed: "تکمیل‌شده",
  cancelled: "لغوشده",
  draft: "پیش‌نویس",
  pending: "در انتظار",
  en_route: "در مسیر مشتری",
  arrived: "رسیده",
  skipped: "انجام‌نشده",
};

function faDate(value: string | null): string {
  if (!value) return "بدون زمان مشخص";
  return formatJalaliDateTime(value);
}

function DriverMap({
  drivers,
}: {
  readonly drivers: readonly BusinessDriverLocation[];
}) {
  const points = drivers.filter(
    (item) => item.latitude !== null && item.longitude !== null,
  );
  if (points.length === 0)
    return (
      <Message kind="info">
        هنوز هیچ راننده‌ای موقعیت خود را ارسال نکرده است.
      </Message>
    );
  const lats = points.map((item) => item.latitude as number);
  const lngs = points.map((item) => item.longitude as number);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const x = (lng: number) =>
    6 + ((lng - minLng) / Math.max(maxLng - minLng, 0.002)) * 88;
  const y = (lat: number) =>
    94 - ((lat - minLat) / Math.max(maxLat - minLat, 0.002)) * 88;
  return (
    <div className="fleet-map" aria-label="نمای موقعیت رانندگان">
      <div className="fleet-map-grid" />
      {points.map((driver) => (
        <div
          className={`fleet-map-point ${driver.isStale ? "stale" : ""}`}
          key={driver.membershipId}
          style={{
            left: `${x(driver.longitude as number)}%`,
            top: `${y(driver.latitude as number)}%`,
          }}
          title={`${driver.name ?? driver.mobileE164} · ${STATUS_FA[driver.status] ?? driver.status}`}
        >
          <span>🚚</span>
          <small>{driver.name ?? "راننده"}</small>
        </div>
      ))}
    </div>
  );
}

export function BusinessConsoleFleetPage(props: {
  readonly businessId: string;
  readonly context: BusinessContextResponse | null;
  readonly api: IdentityApiClient;
  readonly onNavigate: (route: IdentityWebRoute) => void;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onAccount: () => void;
}) {
  const canRead =
    props.context?.effectiveCapabilities.includes("dispatch.read") === true;
  const canAssign =
    props.context?.effectiveCapabilities.includes("dispatch.assign") === true;
  const [drivers, setDrivers] = useState<readonly BusinessDriverLocation[]>([]);
  const [candidates, setCandidates] = useState<readonly DispatchCandidate[]>(
    [],
  );
  const [runs, setRuns] = useState<readonly DispatchRunSummary[]>([]);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [driverId, setDriverId] = useState("");
  const [serviceDate, setServiceDate] = useState(() =>
    jalaliPartsToGregorianDate(nowJalaliParts()),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canRead) return;
    try {
      const [driverData, candidateData, runData] = await Promise.all([
        props.api.getBusinessDrivers(props.businessId),
        props.api.getDispatchCandidates(props.businessId),
        props.api.getBusinessDispatchRuns(props.businessId),
      ]);
      setDrivers(driverData.items);
      setCandidates(candidateData.items);
      setRuns(runData.items);
      setError(null);
    } catch (reason) {
      setError(identityErrorMessage(reason));
    }
  }, [canRead, props.api, props.businessId]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);
  const onlineCount = useMemo(
    () => drivers.filter((item) => !item.isStale).length,
    [drivers],
  );

  const plan = async () => {
    if (!selected.length) {
      setError("حداقل یک سفارش را برای مسیر انتخاب کنید.");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const run = await props.api.planBusinessDispatchRun(props.businessId, {
        serviceRequestIds: selected,
        serviceDate,
        driverMembershipId: driverId || null,
      });
      setInfo(
        `مسیر با ${run.stops.length.toLocaleString("fa-IR")} توقف ساخته شد.`,
      );
      setSelected([]);
      await load();
    } catch (reason) {
      setError(identityErrorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (runId: string, membershipId: string) => {
    if (!membershipId) return;
    setBusy(true);
    setError(null);
    try {
      await props.api.assignBusinessDispatchRun(
        props.businessId,
        runId,
        membershipId,
      );
      setInfo("مأموریت به راننده ارجاع شد.");
      await load();
    } catch (reason) {
      setError(identityErrorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (runId: string) => {
    const reason = window.prompt("دلیل لغو مأموریت را بنویسید:")?.trim() ?? "";
    if (!reason) return;
    if (reason.length < 5) {
      setError("دلیل لغو باید حداقل ۵ نویسه باشد.");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await props.api.cancelBusinessDispatchRun(
        props.businessId,
        runId,
        reason,
      );
      setInfo("مأموریت لغو و به مشتری و تیم عملیات اطلاع داده شد.");
      await load();
    } catch (reasonError) {
      setError(identityErrorMessage(reasonError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <props.Shell
      title="مرکز ناوگان و جمع‌آوری"
      description="رانندگان، سفارش‌های آماده، مسیر هوشمند و ارجاع مأموریت در یک صفحه."
      onAccount={props.onAccount}
      eyebrow="کنسول مدیریت · عملیات"
    >
      <ConsoleNav
        current={IDENTITY_WEB_ROUTES.businessConsoleFleet}
        onNavigate={props.onNavigate}
      />
      {!canRead ? (
        <Message kind="error">
          نقش شما مجوز مشاهده ناوگان و عملیات را ندارد.
        </Message>
      ) : (
        <>
          {error ? <Message kind="error">{error}</Message> : null}
          {info ? <Message kind="info">{info}</Message> : null}
          <div className="fleet-stats">
            <span>
              <strong>{drivers.length.toLocaleString("fa-IR")}</strong> راننده
            </span>
            <span>
              <strong>{onlineCount.toLocaleString("fa-IR")}</strong> آنلاین
            </span>
            <span>
              <strong>{candidates.length.toLocaleString("fa-IR")}</strong> آماده
              مسیر
            </span>
            <span>
              <strong>{runs.length.toLocaleString("fa-IR")}</strong> مأموریت
            </span>
          </div>
          <section className="fleet-section">
            <h2>وضعیت زنده رانندگان</h2>
            <DriverMap drivers={drivers} />
            <div className="fleet-driver-list">
              {drivers.map((driver) => (
                <article key={driver.membershipId}>
                  <strong>{driver.name ?? "راننده"}</strong>
                  <span>{driver.mobileE164}</span>
                  <span
                    className={
                      driver.isStale ? "fleet-offline" : "fleet-online"
                    }
                  >
                    {STATUS_FA[driver.status] ?? driver.status}
                  </span>
                  <small>
                    {driver.recordedAt
                      ? `آخرین موقعیت: ${faDate(driver.recordedAt)}`
                      : "بدون موقعیت ثبت‌شده"}
                  </small>
                </article>
              ))}
            </div>
          </section>
          <section className="fleet-section">
            <h2>ساخت مسیر جمع‌آوری</h2>
            {!canAssign ? (
              <Message kind="info">
                این بخش فقط برای مالک یا مدیر عملیات دارای مجوز ارجاع فعال است.
              </Message>
            ) : (
              <>
                <div className="fleet-plan-controls">
                  <JalaliDateField
                    label="تاریخ شمسی مأموریت"
                    valueGregorian={serviceDate}
                    onChangeGregorian={setServiceDate}
                    required
                    futureYears={2}
                  />
                  <label>
                    راننده (اختیاری)
                    <select
                      value={driverId}
                      onChange={(e) => setDriverId(e.target.value)}
                    >
                      <option value="">بعداً ارجاع می‌دهم</option>
                      {drivers.map((driver) => (
                        <option
                          value={driver.membershipId}
                          key={driver.membershipId}
                        >
                          {driver.name ?? driver.mobileE164}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => void plan()}
                    disabled={busy || !selected.length}
                  >
                    {busy
                      ? "در حال انجام…"
                      : `ساخت مسیر (${selected.length.toLocaleString("fa-IR")})`}
                  </button>
                </div>
                <div className="fleet-candidates">
                  {candidates.length ? (
                    candidates.map((item) => (
                      <label
                        key={item.serviceRequestId}
                        className={
                          selected.includes(item.serviceRequestId)
                            ? "selected"
                            : ""
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(item.serviceRequestId)}
                          onChange={(e) =>
                            setSelected((current) =>
                              e.target.checked
                                ? [...current, item.serviceRequestId]
                                : current.filter(
                                    (id) => id !== item.serviceRequestId,
                                  ),
                            )
                          }
                        />
                        <span>
                          <strong>
                            {item.customerName ?? item.customerMobileE164}
                          </strong>
                          <small>
                            {item.addressText ?? "نشانی متنی ثبت نشده"} ·{" "}
                            {item.itemCount.toLocaleString("fa-IR")} مورد
                          </small>
                          <small>{faDate(item.scheduledPickupAt)}</small>
                        </span>
                      </label>
                    ))
                  ) : (
                    <Message kind="info">
                      سفارش دارای نشانی دقیق و آماده جمع‌آوری وجود ندارد.
                    </Message>
                  )}
                </div>
              </>
            )}
          </section>
          <section className="fleet-section">
            <h2>مأموریت‌های اخیر</h2>
            <div className="fleet-runs">
              {runs.map((run) => (
                <article key={run.id}>
                  <header>
                    <strong>
                      {formatJalaliGregorianDate(run.serviceDate)}
                    </strong>
                    <span>{STATUS_FA[run.status] ?? run.status}</span>
                  </header>
                  <p>
                    {run.stops.length.toLocaleString("fa-IR")} توقف ·{" "}
                    {run.routeDistanceMeters === null
                      ? "فاصله نامشخص"
                      : `${Math.round(run.routeDistanceMeters / 1000).toLocaleString("fa-IR")} کیلومتر`}
                  </p>
                  <p>راننده: {run.driverName ?? "ارجاع نشده"}</p>
                  {run.startedAt ? (
                    <small>شروع: {faDate(run.startedAt)}</small>
                  ) : null}
                  {run.completedAt ? (
                    <small>پایان: {faDate(run.completedAt)}</small>
                  ) : null}
                  {run.cancellationReason ? (
                    <Message kind="info">
                      دلیل لغو: {run.cancellationReason}
                    </Message>
                  ) : null}
                  {canAssign &&
                  run.status !== "completed" &&
                  run.status !== "cancelled" ? (
                    <>
                      <select
                        value={run.driverMembershipId ?? ""}
                        disabled={busy}
                        onChange={(e) => void assign(run.id, e.target.value)}
                      >
                        <option value="">ارجاع به راننده…</option>
                        {drivers.map((driver) => (
                          <option
                            value={driver.membershipId}
                            key={driver.membershipId}
                          >
                            {driver.name ?? driver.mobileE164}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="secondary"
                        disabled={busy}
                        onClick={() => void cancel(run.id)}
                      >
                        لغو مأموریت
                      </button>
                    </>
                  ) : null}
                  <ol>
                    {run.stops.map((stop) => (
                      <li key={stop.id}>
                        {stop.customerName ?? stop.customerMobileE164} —{" "}
                        {stop.addressText ?? "نشانی ثبت نشده"} ·{" "}
                        {STATUS_FA[stop.status] ?? stop.status}
                      </li>
                    ))}
                  </ol>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </props.Shell>
  );
}
